'use client';

import { useState, useEffect, useCallback } from 'react';

import { useAuth } from '@/providers/userContext';

import { fetchJson } from './fetchJson';
import {
  isCacheValid,
  isSaved as cacheIsSaved,
  isVisited as cacheIsVisited,
  getVisitTimestamp as cacheGetVisitTimestamp,
  initializeCache,
  saveHeart as cacheSaveHeart,
  unsaveHeart as cacheUnsaveHeart,
  updateSyncTime,
  markVisited as cacheMarkVisited,
  subscribe as subscribeCache,
  isGuestSaved,
  saveHeartAsGuest,
  unsaveHeartAsGuest,
} from './interactionCache';
import { addToQueue } from './offlineQueue';

/**
 * Hook for managing heart interactions (save/visit)
 * Provides optimistic UI updates and handles offline scenarios
 */
export function useHeartInteractions(heartId) {
  const { user } = useAuth();

  const [saved, setSaved] = useState(false);
  const [visited, setVisited] = useState(false);
  const [visitTimestamp, setVisitTimestamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [visiting, setVisiting] = useState(false);

  // Sync interactions from server on login
  useEffect(() => {
    // User not authenticated - use guest saved state
    if (!user || !user.id) {
      if (heartId) {
        setSaved(isGuestSaved(heartId));
      } else {
        setSaved(false);
      }
      setVisited(false);
      setVisitTimestamp(null);
      setLoading(false);
      return;
    }

    // Reset loading state when user changes (e.g., login transition)
    setLoading(true);

    let cancelled = false;
    const userId = user.id;

    // Check if cache is valid
    if (isCacheValid(userId)) {
      // Use cached data
      if (heartId) {
        setSaved(cacheIsSaved(heartId));
        setVisited(cacheIsVisited(heartId));
        setVisitTimestamp(cacheGetVisitTimestamp(heartId));
      }
      setLoading(false);
      return;
    }

    // Fetch from server
    fetchJson('/api/me/interactions', { method: 'GET' })
      .then((data) => {
        if (!cancelled) {
          initializeCache(userId, data);
          if (heartId) {
            setSaved(cacheIsSaved(heartId));
            setVisited(cacheIsVisited(heartId));
            setVisitTimestamp(cacheGetVisitTimestamp(heartId));
          }
        }
      })
      .catch((error) => {
        console.error('Failed to fetch interactions:', error);
        // Fallback to cache even if stale
        if (heartId) {
          setSaved(cacheIsSaved(heartId));
          setVisited(cacheIsVisited(heartId));
          setVisitTimestamp(cacheGetVisitTimestamp(heartId));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, heartId]);

  // Update state from cache when heartId changes
  useEffect(() => {
    if (!heartId) {
      setSaved(false);
      setVisited(false);
      setVisitTimestamp(null);
      return;
    }

    // Guest user - check guest saves
    if (!user) {
      setSaved(isGuestSaved(heartId));
      setVisited(false);
      setVisitTimestamp(null);
      return;
    }

    // Authenticated user - check cache
    setSaved(cacheIsSaved(heartId));
    setVisited(cacheIsVisited(heartId));
    setVisitTimestamp(cacheGetVisitTimestamp(heartId));
  }, [heartId, user]);

  // Subscribe to cache changes for this heart
  useEffect(() => {
    if (!heartId) return undefined;
    const unsubscribe = subscribeCache(() => {
      if (!user) {
        // Guest user - check guest saves
        setSaved(isGuestSaved(heartId));
        return;
      }
      // Authenticated user - check cache
      setSaved(cacheIsSaved(heartId));
      setVisited(cacheIsVisited(heartId));
      setVisitTimestamp(cacheGetVisitTimestamp(heartId));
    });
    return unsubscribe;
  }, [heartId, user]);

  /**
   * Save or unsave a heart
   */
  const toggleSave = useCallback(async () => {
    if (saving || !heartId) return;

    const newSavedState = !saved;

    // Guest user - save locally only
    if (!user || !user.id) {
      if (newSavedState) {
        saveHeartAsGuest(heartId);
      } else {
        unsaveHeartAsGuest(heartId);
      }
      setSaved(newSavedState);
      return;
    }

    // Authenticated user - save to server
    // Optimistic update - update cache and UI immediately
    // Keep this state even if API fails - user should see correct UI
    if (newSavedState) {
      cacheSaveHeart(heartId);
    } else {
      cacheUnsaveHeart(heartId);
    }
    setSaved(newSavedState);
    setSaving(true);

    try {
      const method = newSavedState ? 'POST' : 'DELETE';
      await fetchJson(`/api/saved/${heartId}`, {
        method,
      });

      // Success - cache already updated, sync time updated
      updateSyncTime();
    } catch (error) {
      // API failed - keep optimistic update, queue for sync
      // User sees correct state immediately, will sync when connection restored
      console.error('Error updating saved status:', error);

      // Queue for sync if offline or server error
      if (!navigator.onLine || error.status >= 500) {
        addToQueue({
          type: newSavedState ? 'save' : 'unsave',
          heartId,
        });
      }
    } finally {
      setSaving(false);
    }
  }, [user, saved, saving, heartId]);

  /**
   * Mark a heart as visited
   */
  const markVisited = useCallback(
    async (timestampOverride = null) => {
      if (!user || !user.id || visited || visiting || !heartId) return;

      const timestamp = timestampOverride || new Date().toISOString();

      // Optimistic update - update cache and UI immediately
      // Keep this state even if API fails - user should see correct UI
      cacheMarkVisited(heartId, timestamp);
      setVisited(true);
      setVisitTimestamp(timestamp);
      setVisiting(true);

      try {
        const data = await fetchJson(`/api/hearts/${heartId}/visit`, {
          method: 'POST',
          body: { timestamp },
        });

        if (data.timestamp) {
          cacheMarkVisited(heartId, data.timestamp);
          setVisitTimestamp(data.timestamp);
        }

        // Success - cache already updated, sync time updated
        updateSyncTime();
      } catch (error) {
        // API failed - keep optimistic update, queue for sync
        // User sees correct state immediately, will sync when connection restored
        console.error('Error marking as visited:', error);

        // Queue for sync if offline or server error
        if (!navigator.onLine || error.status >= 500) {
          addToQueue({
            type: 'visit',
            heartId,
            timestamp,
          });
        }
      } finally {
        setVisiting(false);
      }
    },
    [user, visited, visiting, heartId]
  );

  return {
    // State
    saved,
    visited,
    visitTimestamp,
    loading,
    saving,
    visiting,
    // Actions
    toggleSave,
    markVisited,
    // User status
    isAuthenticated: !!user,
  };
}
